import axios from "axios";
import { doc, getDoc, setDoc, onSnapshot, collection } from "firebase/firestore";
import { get as idbGet, set as idbSet } from "idb-keyval";
import { firestore, auth } from "@/firebaseConfig";
import { PRIMARY_API_HOST, FALLBACK_API_HOST, API_HOST } from "@/config/api";
import {
  normalizeUrl,
  resolveLink,
  chapterIdFor,
  legacyChapterIdFor,
} from "@/util/chapterUrl";
import { backgroundFetch } from "@/util/backgroundFetch";
import { clientRequestQueue, PRIORITY } from "@/services/requestQueue";
import { queryClient } from "@/services/queryClient";

// Task 4.7 sets CLIENT_WRITES_CHAPTERS = false once server writes Firestore directly
export const CLIENT_WRITES_CHAPTERS = false;

const IDB_CHAPTER_PREFIX = "shamudeen_ch_v2:";
const inflightChapterPromises = new Map();
const sessionMetrics = [];

function nowMs() {
  return typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();
}

function recordMetric({ chapterId, source, ms, priority }) {
  if (priority !== "user") return;
  sessionMetrics.push({
    chapterId,
    source,
    ms: Math.round(ms),
    timestamp: Date.now(),
  });
}

export function getSessionMetrics() {
  const total = sessionMetrics.length;
  const cacheHits = sessionMetrics.filter(
    (m) => m.source === "L0" || m.source === "L1" || m.source === "L2"
  ).length;
  const l0Hits = sessionMetrics.filter((m) => m.source === "L0").length;
  const l1Hits = sessionMetrics.filter((m) => m.source === "L1").length;
  const l2Hits = sessionMetrics.filter((m) => m.source === "L2").length;
  const apiHits = sessionMetrics.filter((m) => m.source === "API").length;
  const avgMs =
    total > 0
      ? Math.round(sessionMetrics.reduce((acc, m) => acc + m.ms, 0) / total)
      : 0;
  return {
    total,
    cacheHits,
    hitRate: total > 0 ? Math.round((cacheHits / total) * 100) : 0,
    bySource: { L0: l0Hits, L1: l1Hits, L2: l2Hits, API: apiHits },
    avgMs,
    entries: [...sessionMetrics],
  };
}

function getFormattedDateForFileName() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

function buildChapterPayload(chapterId, normalizedUrl, meta, html = null, source = "L2") {
  const backUrl = meta.backUrl || "";
  let forwardUrl = meta.forwardUrl || "";
  const backAbsUrl =
    meta.backAbsUrl || resolveLink(backUrl, normalizedUrl) || null;
  let forwardAbsUrl =
    meta.forwardAbsUrl || resolveLink(forwardUrl, normalizedUrl) || null;

  if (
    (forwardAbsUrl && backAbsUrl && forwardAbsUrl === backAbsUrl) ||
    (forwardAbsUrl && forwardAbsUrl === normalizedUrl) ||
    (forwardUrl && backUrl && forwardUrl === backUrl)
  ) {
    forwardUrl = "";
    forwardAbsUrl = null;
  }

  return {
    chapterId,
    sourceUrl: meta.sourceUrl || normalizedUrl,
    html: html || null,
    htmlURL: meta.htmlURL || meta.url || null,
    backUrl,
    forwardUrl,
    backAbsUrl,
    forwardAbsUrl,
    contentInline: Boolean(html || meta.contentInline),
    source,
    jobId: meta.jobId || null,
  };
}

async function saveToCacheLayers(chapterId, payload) {
  const cached = { ...payload };
  delete cached.ms;
  queryClient.setQueryData(["chapter", chapterId], cached);
  if (typeof indexedDB !== "undefined") {
    try {
      await idbSet(`${IDB_CHAPTER_PREFIX}${chapterId}`, {
        ...cached,
        cachedAt: Date.now(),
      });
    } catch (_e) {
      // Ignore IndexedDB errors
    }
  }
}

async function readFromL1(chapterId) {
  if (typeof indexedDB === "undefined") return null;
  try {
    const item = await idbGet(`${IDB_CHAPTER_PREFIX}${chapterId}`);
    if (!item) return null;
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (item.cachedAt && Date.now() - item.cachedAt > sevenDaysMs) {
      return null;
    }
    return item;
  } catch (_e) {
    return null;
  }
}

async function readFromFirestore(chapterId, legacyId, normalizedUrl, withContent = true) {
  let docIdUsed = chapterId;
  let metaSnap = null;

  try {
    metaSnap = await getDoc(doc(firestore, "chapters", chapterId));
    if (!metaSnap.exists() && legacyId && legacyId !== chapterId) {
      metaSnap = await getDoc(doc(firestore, "chapters", legacyId));
      docIdUsed = legacyId;
    }
  } catch (_e) {
    return null;
  }

  if (!metaSnap || !metaSnap.exists()) {
    return null;
  }

  const meta = metaSnap.data();
  let isReady = meta.status === "ready" || Boolean(meta.htmlURL);

  let html = null;
  if (withContent) {
    try {
      const contentSnap = await getDoc(doc(firestore, "chapterContent", docIdUsed));
      if (contentSnap.exists()) {
        html = contentSnap.data().html || null;
        if (html) {
          isReady = true;
        }
      }
    } catch (_e) {
      // Ignore
    }
  }

  if (!isReady) {
    return null;
  }

  if (!html && !meta.htmlURL && withContent) {
    return null;
  }

  return buildChapterPayload(chapterId, normalizedUrl, meta, html, "L2");
}

async function getAuthHeaders() {
  try {
    if (auth && auth.currentUser && typeof auth.currentUser.getIdToken === "function") {
      const token = await auth.currentUser.getIdToken();
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
    }
  } catch (_e) {
    // Ignore token errors
  }
  return {};
}

function waitForChapterReady(
  chapterId,
  legacyId,
  normalizedUrl,
  jobId,
  withContent = true,
  apiHost = API_HOST
) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubSnapshot = null;
    let pollTimer = null;
    const startedAt = Date.now();
    const maxDurationMs = 45 * 1000;
    let delayMs = 1500;
    const maxDelayMs = 10000;

    const cleanup = () => {
      settled = true;
      if (unsubSnapshot) {
        try {
          unsubSnapshot();
        } catch (_e) {
          // Ignore
        }
        unsubSnapshot = null;
      }
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const finishWithPayload = async (payload) => {
      if (settled) return;
      cleanup();
      await saveToCacheLayers(chapterId, payload);
      resolve(payload);
    };

    const failWithError = (err) => {
      if (settled) return;
      cleanup();
      reject(err);
    };

    // 1. Real-time Firestore listener (Phase 4 server write)
    try {
      const chapterDocRef = doc(firestore, "chapters", chapterId);
      unsubSnapshot = onSnapshot(
        chapterDocRef,
        async (snap) => {
          if (settled || !snap.exists()) return;
          const data = snap.data();

          // If waiting for a specific render job, ignore stale documents from previous jobs
          if (jobId && data.jobId !== jobId) {
            return;
          }

          if (data.status === "ready" || (!jobId && data.htmlURL)) {
            const loaded = await readFromFirestore(
              chapterId,
              legacyId,
              normalizedUrl,
              withContent
            );
            if (loaded) {
              loaded.source = "API";
              await finishWithPayload(loaded);
            }
          } else if (data.status === "failed") {
            const fallback = await readFromFirestore(
              chapterId,
              legacyId,
              normalizedUrl,
              withContent
            );
            if (fallback) {
              fallback.source = "API";
              await finishWithPayload(fallback);
              return;
            }
            failWithError(new Error(data.error || "Chapter render failed"));
          }
        },
        () => {
          // Ignore snapshot listener permission/offline error and rely on polling
        }
      );
    } catch (_e) {
      // onSnapshot setup optional if mocked
    }

    // 2. Exponential backoff polling on /fetch-status/:jobId
    if (!jobId) {
      return;
    }

    const checkPoll = async () => {
      if (settled) return;
      if (Date.now() - startedAt > maxDurationMs) {
        failWithError(new Error("Polling timed out after 5 minutes"));
        return;
      }
      try {
        const res = await axios.get(`${apiHost}/fetch-status/${jobId}`);
        const job = res.data;
        if (job.status === "completed") {
          if (CLIENT_WRITES_CHAPTERS && job.data?.url) {
            try {
              await setDoc(
                doc(collection(firestore, "chapters"), chapterId),
                {
                  htmlURL: job.data.url,
                  backUrl: job.data.backUrl || "",
                  forwardUrl: job.data.forwardUrl || "",
                },
                { merge: true }
              );
            } catch (_e) {
              // Ignore if rules disallow client write
            }
          }
          const fromFs = await readFromFirestore(
            chapterId,
            legacyId,
            normalizedUrl,
            withContent
          );
          if (fromFs && (!jobId || fromFs.jobId === jobId)) {
            fromFs.source = "API";
            await finishWithPayload(fromFs);
            return;
          }
          if (job.data) {
            const built = buildChapterPayload(
              chapterId,
              normalizedUrl,
              {
                htmlURL: job.data.url,
                backUrl: job.data.backUrl,
                forwardUrl: job.data.forwardUrl,
                jobId,
              },
              job.data.html || null,
              "API"
            );
            await finishWithPayload(built);
            return;
          }
          if (fromFs) {
            fromFs.source = "API";
            await finishWithPayload(fromFs);
            return;
          }
        } else if (job.status === "failed") {
          const fromFs = await readFromFirestore(
            chapterId,
            legacyId,
            normalizedUrl,
            withContent
          );
          if (fromFs) {
            fromFs.source = "API";
            await finishWithPayload(fromFs);
            return;
          }
          failWithError(new Error(job.error || "Chapter processing failed"));
          return;
        } else if (job.status === "unknown") {
          const fromFs = await readFromFirestore(
            chapterId,
            legacyId,
            normalizedUrl,
            withContent
          );
          if (fromFs) {
            fromFs.source = "API";
            await finishWithPayload(fromFs);
          } else {
            failWithError(new Error("Job status unknown and chapter not in Firestore"));
          }
          return;
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          const fromFs = await readFromFirestore(
            chapterId,
            legacyId,
            normalizedUrl,
            withContent
          );
          if (fromFs) {
            fromFs.source = "API";
            await finishWithPayload(fromFs);
          } else {
            failWithError(new Error("Job not found (404) and chapter not in Firestore"));
          }
          return;
        }
      }

      if (!settled) {
        delayMs = Math.min(maxDelayMs, Math.round(delayMs * 1.5));
        pollTimer = setTimeout(checkPoll, delayMs);
      }
    };

    pollTimer = setTimeout(checkPoll, delayMs);
  });
}

async function callRenderApi(host, payload, headers, persistRetry) {
  try {
    const res = await backgroundFetch(
      `${host}/chapters/render`,
      {
        method: "POST",
        headers,
        data: payload,
      },
      {
        persistRetry,
      }
    );
    return res.data;
  } catch (err) {
    if (err.response && err.response.status === 404) {
      const legacyRes = await backgroundFetch(
        `${host}/fetch-rendered-text`,
        {
          method: "POST",
          headers,
          data: payload,
        },
        {
          persistRetry,
        }
      );
      return legacyRes.data;
    }
    throw err;
  }
}

async function requestRenderFromServer({
  normalizedUrl,
  chapterId,
  legacyId,
  book,
  priority,
  force,
  withContent,
}) {
  const queuePriority =
    priority === "prefetch" ? PRIORITY.PREFETCH : PRIORITY.USER;

  return clientRequestQueue.enqueue(
    chapterId,
    async () => {
      const headers = await getAuthHeaders();
      const filename =
        ((book && book.name) || "chapter").replaceAll(" ", "_") +
        getFormattedDateForFileName() +
        ".html";
      const isRoyalRoad = normalizedUrl.includes("royalroad.com");
      const defaultSelector = isRoyalRoad
        ? ".chapter-inner.chapter-content"
        : ".post__body";

      const payload = {
        url: normalizedUrl,
        selector:
          book?.selector && book.selector !== ".post__body"
            ? book.selector
            : (isRoyalRoad ? defaultSelector : (book?.selector || ".post__body")),
        back_selector: book?.back_selector || (isRoyalRoad ? "a.btn:has(.fa-chevron-left), a:contains('Previous Chapter'), a.btn:contains('Previous')" : ""),
        forward_selector: book?.forward_selector || (isRoyalRoad ? "a.btn:has(.fa-chevron-right), a:contains('Next Chapter'), a.btn:contains('Next')" : ""),
        priority,
        force: Boolean(force),
        filename,
      };

            let responseData;
      let activeHost = PRIMARY_API_HOST;

      if (PRIMARY_API_HOST !== FALLBACK_API_HOST) {
        try {
          responseData = await callRenderApi(
            PRIMARY_API_HOST,
            payload,
            headers,
            false
          );
          activeHost = PRIMARY_API_HOST;
        } catch (primaryErr) {
          console.warn(
            `[Reader] Primary scraper (${PRIMARY_API_HOST}) failed (${primaryErr.message}). Falling back to Cloud Run (${FALLBACK_API_HOST})...`
          );
          responseData = await callRenderApi(
            FALLBACK_API_HOST,
            payload,
            headers,
            priority === "user"
          );
          activeHost = FALLBACK_API_HOST;
        }
      } else {
        responseData = await callRenderApi(
          PRIMARY_API_HOST,
          payload,
          headers,
          priority === "user"
        );
        activeHost = PRIMARY_API_HOST;
      }
      if (responseData?.status === "ready") {
        if (responseData.html || responseData.url) {
          const readyChapter = buildChapterPayload(
            chapterId,
            normalizedUrl,
            {
              htmlURL: responseData.url,
              backUrl: responseData.backUrl,
              forwardUrl: responseData.forwardUrl,
              backAbsUrl: responseData.backAbsUrl,
              forwardAbsUrl: responseData.forwardAbsUrl,
            },
            responseData.html || null,
            "API"
          );
          await saveToCacheLayers(chapterId, readyChapter);
          return readyChapter;
        }

        const readyChapter = await readFromFirestore(
          chapterId,
          legacyId,
          normalizedUrl,
          withContent
        );
        if (readyChapter) {
          readyChapter.source = "API";
          await saveToCacheLayers(chapterId, readyChapter);
          return readyChapter;
        }
      }

      if (responseData?.url) {
        const immediate = buildChapterPayload(
          chapterId,
          normalizedUrl,
          {
            htmlURL: responseData.url,
            backUrl: responseData.backUrl,
            forwardUrl: responseData.forwardUrl,
          },
          responseData.html || null,
          "API"
        );
        await saveToCacheLayers(chapterId, immediate);
        return immediate;
      }

            if (responseData?.jobId) {
        return waitForChapterReady(
          chapterId,
          legacyId,
          normalizedUrl,
          responseData.jobId,
          withContent,
          activeHost
        );
      }
      throw new Error("Unexpected response from render API");
    },
    queuePriority
  );
}

/**
 * Synchronously peek L0 memory cache for a chapter URL.
 * @param {string} rawUrl
 * @returns {object|undefined}
 */
export function peekChapter(rawUrl) {
  if (!rawUrl) return undefined;
  const chapterId = chapterIdFor(rawUrl);
  const cached = queryClient.getQueryData(["chapter", chapterId]);
  if (cached && (cached.html || cached.htmlURL)) {
    return { ...cached, source: "L0" };
  }
  return undefined;
}

/**
 * Load a chapter using L0 (memory) -> L1 (IndexedDB) -> L2 (Firestore) -> API.
 * Concurrent calls for the same normalized URL are deduplicated.
 * @param {string} rawUrl
 * @param {object} options
 * @returns {Promise<object>}
 */
export async function getChapter(
  rawUrl,
  { priority = "user", book = {}, force = false, withContent = true } = {}
) {
  const start = nowMs();
  const normalizedUrl = normalizeUrl(rawUrl);
  const chapterId = chapterIdFor(rawUrl);
  const legacyId = legacyChapterIdFor(rawUrl);

  if (!normalizedUrl) {
    throw new Error("Invalid chapter URL");
  }

  // 1. Check L0 memory cache
  if (!force) {
    const l0 = queryClient.getQueryData(["chapter", chapterId]);
    if (l0 && (!withContent || l0.html || l0.htmlURL)) {
      const ms = nowMs() - start;
      recordMetric({ chapterId, source: "L0", ms, priority });
      return { ...l0, source: "L0", ms };
    }
  }

  // 2. Deduplicate in-flight loads for the same chapterId
  if (!force && inflightChapterPromises.has(chapterId)) {
    if (priority === "user") {
      // Promote any queued request in clientRequestQueue
      const existing = inflightChapterPromises.get(chapterId);
      if (existing.promoteToUser) {
        existing.promoteToUser();
      }
    }
    const result = await inflightChapterPromises.get(chapterId).promise;
    const ms = nowMs() - start;
    recordMetric({ chapterId, source: result.source, ms, priority });
    return { ...result, ms };
  }

  const loadTask = (async () => {
    if (!force) {
      // Check L1 IndexedDB
      const l1 = await readFromL1(chapterId);
      if (l1 && (!withContent || l1.html || l1.htmlURL)) {
        queryClient.setQueryData(["chapter", chapterId], l1);
        return { ...l1, source: "L1" };
      }

      // Check L2 Firestore
      const l2 = await readFromFirestore(
        chapterId,
        legacyId,
        normalizedUrl,
        withContent
      );
      if (l2) {
        await saveToCacheLayers(chapterId, l2);
        return { ...l2, source: "L2" };
      }
    }

    // Miss or force -> request render from API
    const rendered = await requestRenderFromServer({
      normalizedUrl,
      chapterId,
      legacyId,
      book,
      priority,
      force,
      withContent,
    });
    return { ...rendered, source: "API" };
  })();

  const entry = {
    promise: loadTask,
    promoteToUser: () => {
      // If queued in clientRequestQueue, bump priority to USER
      if (clientRequestQueue.waitingQueue.some((w) => w.key === chapterId)) {
        clientRequestQueue.enqueue(chapterId, () => loadTask, PRIORITY.USER).catch(() => {});
      }
    },
  };

  inflightChapterPromises.set(chapterId, entry);

  try {
    const result = await loadTask;
    const ms = nowMs() - start;
    recordMetric({ chapterId, source: result.source, ms, priority });
    return { ...result, ms };
  } finally {
    inflightChapterPromises.delete(chapterId);
  }
}

/**
 * Fire-and-forget prefetch for a chapter URL.
 * @param {string} rawUrl
 * @param {object} options
 * @returns {Promise<object|null>}
 */
export function prefetchChapter(
  rawUrl,
  { book = {}, withContent = true } = {}
) {
  if (!rawUrl) return Promise.resolve(null);
  return getChapter(rawUrl, {
    priority: "prefetch",
    book,
    force: false,
    withContent,
  }).catch(() => null);
}

/**
 * Wrap raw chapter HTML fragment in a self-contained reader document for iframe srcdoc.
 * @param {string} htmlFragment
 * @returns {string}
 */
export function wrapReaderHtml(htmlFragment) {
  if (!htmlFragment) return "";
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <base target="_blank">
  <style>
    *, *::before, *::after {
      box-sizing: border-box;
    }
    html, body {
      margin: 0;
      padding: 20px 24px;
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 18px;
      line-height: 1.75;
      color: #1e293b;
      background: #ffffff;
      max-width: 780px;
      margin-left: auto;
      margin-right: auto;
      word-wrap: break-word;
      overflow-wrap: break-word;
      word-break: break-word;
      overflow-x: hidden;
      width: 100%;
    }
    img, video, iframe, table, pre {
      max-width: 100%;
      height: auto;
    }
    pre {
      white-space: pre-wrap;
      word-break: break-all;
    }
    table {
      display: block;
      overflow-x: auto;
      max-width: 100%;
    }
    p {
      margin: 0 0 1.15em 0;
    }
    a {
      color: #0284c7;
    }
    @media (max-width: 600px) {
      html, body {
        padding: 16px 14px;
        font-size: 17px;
        line-height: 1.65;
      }
    }
  </style>
</head>
<body>${htmlFragment}</body>
</html>`;
}
